// Manager RUM (Real User Monitoring) client.
//
// Buffers page-load timings, API errors and uncaught exceptions, then flushes
// them in a batch to /manager-api/telemetry every FLUSH_INTERVAL_MS or when
// the buffer reaches MAX_BUFFER. The backend buckets labels server-side so
// the route names sent here just have to match its allow-list.
//
// Why client-side: prom-client is Node-only, can't run in a browser. The
// browser pushes raw samples; the Go backend converts to Prometheus.

import api from './api'

interface PageLoadSample {
  route: string
  duration: number
}

interface ApiErrorSample {
  route: string
  statusClass: '4xx' | '5xx' | 'net'
}

interface PageErrorSample {
  route: string
  category: 'uncaught' | 'render' | 'chunk' | 'network' | 'unknown' | 'primevue-overlay-align'
}

const FLUSH_INTERVAL_MS = 30_000
const MAX_BUFFER = 50

// Stale-shell recovery: when a dynamic-import fails because the deployed
// build has new chunk hashes, force one reload to fetch the fresh
// index.html. The sessionStorage marker prevents reload loops when the
// chunk is genuinely missing for a reason other than stale cache.
const CHUNK_RELOAD_KEY = 'neogo-chunk-reload-at'
const CHUNK_RELOAD_COOLDOWN_MS = 30_000

function recoverFromStaleShell() {
  if (typeof window === 'undefined') return
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0)
    if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()))
  } catch {
    // Storage disabled (private mode) — still reload once; worst case the
    // user sees the same error twice in a row, but the page stays usable.
  }
  window.location.reload()
}

let pageLoads: PageLoadSample[] = []
let apiErrors: ApiErrorSample[] = []
let pageErrors: PageErrorSample[] = []
let flushTimer: ReturnType<typeof setInterval> | null = null

// Map Vue route name → backend allow-list slug. Anything else → 'other' on the
// server side, so this list only needs entries that diverge from Vue's name.
const routeNameMap: Record<string, string> = {
  Dashboard: 'dashboard',
  Instances: 'instances',
  InstanceDetail: 'instance-detail',
  Chat: 'chat',
  SendTest: 'send-test',
  EventMonitor: 'events',
  Failover: 'failover',
  Alerting: 'alerts',
  Health: 'health',
  Voip: 'voip',
  Logs: 'logs',
  Users: 'users',
  Teams: 'teams',
  Roles: 'roles',
  Inbox: 'inbox',
  Analytics: 'analytics',
  Contacts: 'contacts',
  CannedResponses: 'canned-responses',
  Automations: 'automations',
  Integrations: 'integrations',
  CustomFields: 'custom-fields',
  Macros: 'macros',
  MCPServers: 'mcp-servers',
  WhatsApp24h: 'whatsapp-24h',
  Settings: 'settings',
  HelpCenter: 'help',
  Groups: 'groups',
  Campaigns: 'campaigns',
  Chatwoot: 'chatwoot',
  Communities: 'communities',
  Login: 'login',
  AdminFeatures: 'admin-features',
  AdminOrgs: 'admin-orgs',
  AdminAudit: 'admin-audit',
}

export function routeKey(name: string | null | undefined): string {
  if (!name) return 'unknown'
  return routeNameMap[name] ?? 'other'
}

export function recordPageLoad(routeName: string | null | undefined, durationMs: number) {
  if (durationMs <= 0 || durationMs > 60_000) return
  pageLoads.push({ route: routeKey(routeName), duration: durationMs / 1000 })
  flushIfFull()
}

export function recordApiError(routeName: string | null | undefined, status: number) {
  let cls: ApiErrorSample['statusClass']
  if (status >= 500) cls = '5xx'
  else if (status >= 400) cls = '4xx'
  else cls = 'net'
  apiErrors.push({ route: routeKey(routeName), statusClass: cls })
  flushIfFull()
}

export function recordPageError(
  routeName: string | null | undefined,
  category: PageErrorSample['category'] = 'uncaught',
) {
  pageErrors.push({ route: routeKey(routeName), category })
  flushIfFull()
}

function flushIfFull() {
  if (pageLoads.length + apiErrors.length + pageErrors.length >= MAX_BUFFER) {
    void flush()
  }
}

async function flush() {
  if (pageLoads.length === 0 && apiErrors.length === 0 && pageErrors.length === 0) return
  const payload = { pageLoads, apiErrors, pageErrors }
  pageLoads = []
  apiErrors = []
  pageErrors = []
  try {
    await api.post('/manager-api/telemetry', payload)
  } catch {
    // Telemetry must never throw into user code. Drop the batch silently —
    // the next sample will trigger another attempt.
  }
}

export function startTelemetry() {
  if (flushTimer) return
  flushTimer = setInterval(() => void flush(), FLUSH_INTERVAL_MS)

  // Best-effort flush on tab close.
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      // Use sendBeacon when available so the request survives unload.
      if ('sendBeacon' in navigator && (pageLoads.length || apiErrors.length || pageErrors.length)) {
        const blob = new Blob(
          [JSON.stringify({ pageLoads, apiErrors, pageErrors })],
          { type: 'application/json' },
        )
        try {
          navigator.sendBeacon('/manager-api/telemetry', blob)
        } catch {
          // ignore
        }
      }
    })

    // Capture uncaught errors and unhandled promise rejections.
    window.addEventListener('error', (ev) => {
      const isChunk = /Loading chunk|Failed to fetch dynamically imported/i.test(ev.message ?? '')
      recordPageError(currentRouteName, isChunk ? 'chunk' : 'uncaught')
      if (isChunk) recoverFromStaleShell()
    })
    window.addEventListener('unhandledrejection', (ev) => {
      const reason = ev.reason
      const message = typeof reason === 'string' ? reason : reason?.message ?? ''
      const isChunk = /Loading chunk|Failed to fetch dynamically imported/i.test(message)
      recordPageError(currentRouteName, isChunk ? 'chunk' : 'uncaught')
      if (isChunk) recoverFromStaleShell()
    })
  }
}

export function stopTelemetry() {
  if (flushTimer) {
    clearInterval(flushTimer)
    flushTimer = null
  }
}

// Tracks the current route so the global error handlers can label samples.
// Updated by the router subscriber wired in main.ts.
let currentRouteName: string | null = null
export function setCurrentRoute(name: string | null) {
  currentRouteName = name
}
